import { Component, Input, Output, EventEmitter, ContentChild, TemplateRef } from "@angular/core"
import { CommonModule } from "@angular/common"
import { MatIconModule } from "@angular/material/icon"
import { DataLoaderComponent } from "../data-loader/data-loader.component" 

export type TableSortOrder = "ASC" | "DESC"

export interface TableColumn {
  key: string
  label: string
  width?: string
  class?: string
  /** When true, header is clickable and emits sortChange. */
  sortable?: boolean
  /** Backend sort_by value; defaults to `key` when sortable. */
  sortKey?: string
}

export interface TableSortChange {
  sortBy: string
  sortOrder: TableSortOrder
}

export interface TableAction {
  icon: string
  label: string
  class?: string
  callback: (row: any) => void
  show?: (row: any) => boolean
}

@Component({
  selector: "app-common-table",
  standalone: true,
  imports: [CommonModule, MatIconModule, DataLoaderComponent],
  templateUrl: "./common-table.component.html",
  styleUrls: ["./common-table.component.css"],
})
export class CommonTableComponent {
  @Input() data: any[] = []
  @Input() columns: TableColumn[] = []
  @Input() actions: TableAction[] = []
  @Input() loading = false
  /** Shown under the radio loader while `loading` is true. */
  @Input() loadingMessage = "Loading data..."
  @Input() emptyMessage = "No data available"
  @Input() emptyDescription = ""
  @Input() showAddButton = false
  @Input() addButtonText = "Add New"
  /** Material icon name for empty state (e.g. `local_offer`). Falls back to default SVG when unset. */
  @Input() emptyIcon: string | null = null
  /**
   * When set (e.g. `"1120px"`), the table keeps at least this width so narrow viewports
   * get horizontal scroll instead of squashed/overlapping columns. Omit for few-column tables.
   */
  @Input() minTableWidth: string | null = null
  @Input() sortBy: string | null = null
  @Input() sortOrder: TableSortOrder | null = null

  @Output() rowClick = new EventEmitter<any>()
  @Output() addClick = new EventEmitter<void>()
  @Output() sortChange = new EventEmitter<TableSortChange>()

  @ContentChild("rowTemplate") rowTemplate?: TemplateRef<any>
  /** Optional custom header row. When provided, the consumer renders all <th> cells (opt-in; default header used otherwise). */
  @ContentChild("headerTemplate") headerTemplate?: TemplateRef<any>

  onRowClick(row: any) {
    this.rowClick.emit(row)
  }

  onAddClick() {
    this.addClick.emit()
  }

  shouldShowAction(action: TableAction, row: any): boolean {
    return action.show ? action.show(row) : true
  }

  trackByIndex(index: number): number {
    return index
  }

  getCellValue(row: any, column: TableColumn): any {
    const keys = column.key.split(".")
    let value = row
    for (const key of keys) {
      value = value?.[key]
    }
    return value
  }

  getColumnSortKey(column: TableColumn): string {
    return column.sortKey ?? column.key
  }

  isSortActive(column: TableColumn): boolean {
    if (!column.sortable || !this.sortBy) return false
    return this.sortBy === this.getColumnSortKey(column)
  }

  onHeaderSort(column: TableColumn): void {
    if (!column.sortable) return
    const key = this.getColumnSortKey(column)
    const nextOrder: TableSortOrder =
      this.sortBy === key && this.sortOrder === "ASC" ? "DESC" : "ASC"
    this.sortChange.emit({ sortBy: key, sortOrder: nextOrder })
  }
}
